import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useGetFbsCustomerType } from "@/app/fbs-hub-app/shared/composables/useGetFbsCustomerType.composable";
import { useGetGasContainers } from "@/app/fbs-hub-app/shared/composables/useGetGasContainers.composable";
import { ALLOWED_BLOCK_VALUES } from "@/app/fbs-hub-app/shared/helper/constants.helper";
import { FbsCustomerType } from "@/app/fbs-hub-app/shared/@types/FbsCustomerType";

export const useGasContainerStore = defineStore("GasContainer", () => {
  const selectedGasContainerSerialNumber = ref<string>();

  const {
    gasContainers,
    meters,
    fetchingGasContainersError,
    isFetchingGasContainers,
    onFetchingGasContainers,
  } = useGetGasContainers();

  const { fbsCustomerType } = useGetFbsCustomerType(gasContainers, meters);

  const gasContainerCount = computed(() => gasContainers.value?.length);

  const fetchGasContainers = async () => {
    await onFetchingGasContainers();

    if (!selectedGasContainer.value && gasContainerCount.value)
      // @ts-expect-error: TODO: This new error showed up after updating @vue/tsconfig. Examine if this is really an issue (or `?` or `!` can be applied easily)
      setSelectedGasContainerSerialNumber(gasContainers.value![0].serialNumber);
  };

  const setSelectedGasContainerSerialNumber = (serialNumber: string) => {
    selectedGasContainerSerialNumber.value = serialNumber;
  };

  const selectedGasContainer = computed(() =>
    gasContainers.value.find(
      (container) =>
        container.serialNumber === selectedGasContainerSerialNumber.value,
    ),
  );

  const gasContainersWithOpenOrders = computed(() =>
    gasContainers.value.filter((container) => container.hasOpenOrder),
  );

  // Containers are only available for an order if they have no open order, a valid gas price and no disallowed block reason
  const gasContainersAvailableForOrder = computed(
    () =>
      gasContainers.value
        // filter out containers with open order
        .filter((container) => !container.hasOpenOrder)
        // filter out containers with missing gas price (except for single meter customers)
        .filter(
          (container) =>
            fbsCustomerType.value === FbsCustomerType.SingleMeterCustomer ||
            (container.gasPrice.excludingVat !== undefined &&
              container.gasPrice.excludingVat > 0),
        )
        // filter out containers with disallowed block reasons
        .filter(
          (container) =>
            ALLOWED_BLOCK_VALUES.includes(container.deliveryBlock) &&
            ALLOWED_BLOCK_VALUES.includes(container.invoiceBlock) &&
            ALLOWED_BLOCK_VALUES.includes(container.deliveryBlockCustomer) &&
            ALLOWED_BLOCK_VALUES.includes(
              container.deliveryBlockCustomerBySalesOrganization,
            ),
        ) || [],
  );

  // Autogas customers are not allowed to order at all, independent of the container properties.
  const enableOrder = computed(() => {
    if (fbsCustomerType.value === FbsCustomerType.AutogasCustomer) return false;

    return true;
  });

  const gasContainerHasMeterContract = computed(
    () => !!gasContainers.value[0]?.meterContractId,
  );

  const resetData = () => {
    selectedGasContainerSerialNumber.value = undefined;
    gasContainers.value = [];
    meters.value = [];
    fetchingGasContainersError.value = undefined;
  };

  return {
    gasContainers,
    selectedGasContainer,
    selectedGasContainerSerialNumber,
    setSelectedGasContainerSerialNumber,
    fetchingGasContainersError,
    isFetchingGasContainers,
    fbsCustomerType,
    gasContainerCount,
    gasContainersWithOpenOrders,
    gasContainersAvailableForOrder,
    enableOrder,
    meters,
    gasContainerHasMeterContract,
    fetchGasContainers,
    resetData,
  };
});
